import type { FC } from 'react';
import { Suspense, useCallback, useDeferredValue, useEffect, useRef } from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';
import TrendsFilterBar from './TrendsFilterBar';
import RedditPostItem from './RedditPostItem';
import RedditPostDetailsDrawer from './RedditPostDetailsDrawer';
import TrendsListSkeleton from './TrendsListSkeleton';
import { useRedditPosts } from '../api';
import useTrendsQueryParams from '../hooks/useTrendsQueryParams';
import { PAGE_SIZE } from '../constants';
import type { PostType, RedditPost, SortBy, TimeRange } from '../types';
import { getDateRangeFromTimeRange } from '../utils';

const TRENDS_PAGE_SCROLL_ROOT_ID = 'trends-page';

interface TrendsPostsGridProps {
  timeRange: TimeRange;
  keywordIds: number[];
  postType: PostType;
  sortBy: SortBy;
  redditId: string | null | undefined;
  onLookDeeper: (post: RedditPost) => void;
  onClearRedditId: () => void;
}

const TrendsPostsGrid: FC<TrendsPostsGridProps> = ({
  timeRange,
  keywordIds,
  postType,
  sortBy,
  redditId,
  onLookDeeper,
  onClearRedditId,
}) => {
  const { date_from, date_to } = getDateRangeFromTimeRange(timeRange);
  const {
    data: { pages, count },
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useRedditPosts({
    date_from,
    date_to,
    keyword_id: keywordIds.length > 0 ? keywordIds : undefined,
    post_type: postType === 'all' ? undefined : postType,
    order_by: sortBy,
    desc: true,
    limit: PAGE_SIZE,
  });

  const posts = pages.flat();
  const selectedPost = redditId ? posts.find((post) => post.reddit_id === redditId) ?? null : null;
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!redditId || selectedPost !== null || isFetchingNextPage) {
      return;
    }

    if (hasNextPage) {
      fetchNextPage().catch(console.error);
      return;
    }

    onClearRedditId();
  }, [redditId, selectedPost, hasNextPage, isFetchingNextPage, fetchNextPage, onClearRedditId]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage().catch(console.error);
        }
      },
      { root: document.querySelector(`#${TRENDS_PAGE_SCROLL_ROOT_ID}`), rootMargin: '600px' }
    );

    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    const trendsPageContainer = document.querySelector(`#${TRENDS_PAGE_SCROLL_ROOT_ID}`);
    if (trendsPageContainer) trendsPageContainer.scrollTop = 0;
  }, [timeRange, keywordIds, postType, sortBy]);

  if (count === 0) {
    return (
      <>
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
          <Typography color="text.secondary">No posts match the selected filters.</Typography>
        </Box>
        <RedditPostDetailsDrawer post={selectedPost} onClose={onClearRedditId} />
      </>
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(420px, 1fr))',
            gap: 2,
          }}
        >
          {posts.map((post) => (
            <RedditPostItem key={post.reddit_id} post={post} onLookDeeper={onLookDeeper} />
          ))}
        </Box>
        <Box ref={sentinelRef} sx={{ height: 1 }} />
        {isFetchingNextPage && (
          <Box sx={{ py: 2 }}>
            <LinearProgress />
          </Box>
        )}
      </Box>

      <RedditPostDetailsDrawer post={selectedPost} onClose={onClearRedditId} />
    </>
  );
};

const Trends: FC = () => {
  const {
    timeRange,
    setTimeRange,
    sortBy,
    setSortBy,
    postType,
    setPostType,
    keywordIds,
    actualKeywordIds,
    setKeywordIds,
    redditId,
    setRedditId,
  } = useTrendsQueryParams();

  const handleLookDeeper = useCallback(
    (post: RedditPost) => {
      setRedditId(post.reddit_id);
    },
    [setRedditId]
  );

  const handleClearRedditId = useCallback(() => {
    setRedditId(undefined);
  }, [setRedditId]);

  // Refetching on a new filter value happens in a transition: the current list stays on screen and the
  // filters stay interactive, so selections can be changed while a request is still in flight.
  const deferredTimeRange = useDeferredValue(timeRange);
  const deferredKeywordIds = useDeferredValue(keywordIds);
  const deferredPostType = useDeferredValue(postType);
  const deferredSortBy = useDeferredValue(sortBy);
  const isRefetchingFilters =
    deferredTimeRange !== timeRange ||
    deferredKeywordIds !== keywordIds ||
    deferredPostType !== postType ||
    deferredSortBy !== sortBy;

  return (
    <>
      <TrendsFilterBar
        timeRange={timeRange}
        keywordIds={actualKeywordIds}
        postType={postType}
        sortBy={sortBy}
        onTimeRangeChange={setTimeRange}
        onKeywordsChange={setKeywordIds}
        onPostTypeChange={setPostType}
        onSortByChange={setSortBy}
      />

      <Box sx={{ px: 3, pb: 4 }}>
        <Box sx={{ height: 4, mb: 1 }}>{isRefetchingFilters && <LinearProgress />}</Box>
        <Suspense fallback={<TrendsListSkeleton />}>
          <TrendsPostsGrid
            timeRange={deferredTimeRange}
            keywordIds={deferredKeywordIds}
            postType={deferredPostType}
            sortBy={deferredSortBy}
            redditId={redditId}
            onLookDeeper={handleLookDeeper}
            onClearRedditId={handleClearRedditId}
          />
        </Suspense>
      </Box>
    </>
  );
};

export default Trends;
